/**
 * Cotizaciones, remisiones y borradores (v2/documentosprevios, spec 2026-10-10 §3.2 y §3.3): reglas de pantalla
 * (window.FDDocumentosPrevios). Funciones puras (Vitest en entorno node, sin DOM); el montaje (DataTables, FD.ajax)
 * vive en application/views/noble/documentosprevios/index.php. Espejo de v2/Documentosprevios y de
 * Documento_previo_model: el servidor siempre vuelve a validar. No importa nada de src/js/cfdi/ (otro bundle).
 */
export const TIPOS = ['cotizacion', 'remision', 'borrador'];
export const ETIQUETA = { borrador: 'Borrador', cotizacion: 'Cotización', remision: 'Remisión' };
/** Estados en los que un documento se edita, se envía, se cancela y se factura (Documento_previo_model::EDITABLES). */
export const EDITABLES = ['abierto', 'enviada', 'aceptada'];
export const MAX_SELECCION = 50;
/** Direcciones por campo (Para y Copia oculta) en el correo de un documento (Correocola::MAX_DIRECCIONES_DOC). */
export const MAX_CORREOS = 5;
// Cancelado en rojo, como el resto de la app (DESIGN.md: «rojo rechazo: cancelado»). Un borrador que se guardó como
// cotización o remisión no se rechazó: «Convertido» va en neutro.
const BADGE = {
  abierto: 'badge-soft-primary', enviada: 'badge-soft-info', aceptada: 'badge-soft-success', vencida: 'badge-soft-warning',
  facturado: 'badge-soft-success', remisionada: 'badge-soft-secondary', cancelado: 'badge-soft-danger', convertido: 'badge-soft-secondary',
};
/**
 * El estado en palabras, con el género del documento (critique 11-oct): la cotización y la remisión son femeninas
 * («Abierta», «Facturada», «Cancelada»), el borrador masculino («Abierto», «Facturado», «Cancelado», «Convertido»).
 * [singular, plural]: el plural nombra las opciones del filtro y el pie de la tabla.
 */
export const ESTADO_UI = {
  cotizacion: {
    abierto: ['Abierta', 'Abiertas'], enviada: ['Enviada', 'Enviadas'], aceptada: ['Aceptada', 'Aceptadas'], vencida: ['Vencida', 'Vencidas'],
    remisionada: ['Remisionada', 'Remisionadas'], facturado: ['Facturada', 'Facturadas'], cancelado: ['Cancelada', 'Canceladas'],
  },
  remision: {
    abierto: ['Abierta', 'Abiertas'], enviada: ['Enviada', 'Enviadas'], facturado: ['Facturada', 'Facturadas'], cancelado: ['Cancelada', 'Canceladas'],
  },
  borrador: {
    abierto: ['Abierto', 'Abiertos'], facturado: ['Facturado', 'Facturados'], cancelado: ['Cancelado', 'Cancelados'], convertido: ['Convertido', 'Convertidos'],
  },
};
/** Filtro por omisión de la lista: lo que todavía se trabaja (abierto, enviado o aceptado; una cotización vencida también,
 *  porque se acepta y se convierte igual). El servidor lo entiende como estado=pendientes (Documento_previo_model::lista). */
export const ESTADO_DEFECTO = 'pendientes';
const ESTADOS = {
  borrador:   ['abierto', 'facturado', 'cancelado'],
  cotizacion: ['abierto', 'enviada', 'aceptada', 'vencida', 'facturado', 'remisionada', 'cancelado'],
  remision:   ['abierto', 'enviada', 'facturado', 'cancelado'],
};
const MESES = ['ene', 'feb', 'mar', 'abr', 'may', 'jun', 'jul', 'ago', 'sep', 'oct', 'nov', 'dic'];
const ICONO = { abrir: 'ti-edit', pdf: 'ti-file-type-pdf', duplicar: 'ti-copy', enviar: 'ti-send', aceptar: 'ti-circle-check',
  convertir_factura: 'ti-file-invoice', convertir_remision: 'ti-truck-delivery', cancelar: 'ti-ban' };
const TEXTO_ACCION = { pdf: 'PDF', duplicar: 'Duplicar', enviar: 'Enviar por correo', aceptar: 'Marcar como aceptada',
  convertir_factura: 'Convertir en factura', convertir_remision: 'Convertir en remisión', cancelar: 'Cancelar' };

export function esc(texto) {
  return String(texto == null ? '' : texto)
    .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;').replace(/'/g, '&#39;');
}

function editable(fila) { return EDITABLES.indexOf(fila && fila.estado) !== -1; }

export function estadosDe(tipo) { return (ESTADOS[tipo] || []).slice(); }

/** {texto, badge} del estado de un documento de ese tipo; uno desconocido sale tal cual, en neutro. */
export function estadoUi(tipo, estado) {
  const e = String(estado || '');
  const t = (ESTADO_UI[tipo] || ESTADO_UI.cotizacion)[e];
  return t ? { texto: t[0], badge: BADGE[e] } : { texto: e, badge: 'badge-soft-secondary' };
}

/** Lo que dice la opción «cancelado» del filtro: en Borradores también reúne a los que se guardaron como cotización o
 *  remisión (quedan cerrados, estado cancelado, y la lista los muestra «Convertido en COT-…»). */
function nombreFiltro(tipo, estado) {
  if (tipo === 'borrador' && estado === 'cancelado') return 'Cancelados o convertidos';
  return (ESTADO_UI[tipo] && ESTADO_UI[tipo][estado]) ? ESTADO_UI[tipo][estado][1] : estado;
}

/** Opciones del filtro de estado: «Pendientes» (la de omisión), «Todas»/«Todos» y cada estado del tipo, en plural. */
export function opcionesEstado(tipo) {
  return [{ valor: ESTADO_DEFECTO, texto: 'Pendientes' }, { valor: '', texto: tipo === 'borrador' ? 'Todos' : 'Todas' }]
    .concat(estadosDe(tipo).map(function (e) { return { valor: e, texto: nombreFiltro(tipo, e) }; }));
}

export function tipoInicial(pedido, permitidos) {
  const p = permitidos || [];
  return p.indexOf(pedido) !== -1 ? pedido : (p[0] || '');
}

/** '2026-10-25' → '25-oct'. (La misma regla que cfdi/documento-previo-logica.js, que vive en el bundle de Crear.) */
export function fechaCorta(iso) {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(String(iso || ''));
  if (!m) return '';
  const mes = parseInt(m[2], 10);
  return (mes >= 1 && mes <= 12) ? parseInt(m[3], 10) + '-' + MESES[mes - 1] : '';
}

/** '2026-10-10 12:34' → '10-oct-2026 12:34'. */
export function fechaTexto(s) {
  const m = /^(\d{4})-(\d{2})-(\d{2})(?:[ T](\d{2}:\d{2}))?/.exec(String(s || ''));
  if (!m) return '—';
  return fechaCorta(s) + '-' + m[1] + (m[4] ? ' ' + m[4] : '');
}

export function totalTexto(total, moneda) {
  const n = Number(total);
  if (total === null || total === undefined || total === '' || !Number.isFinite(n)) return '—';
  const partes = Math.abs(n).toFixed(2).split('.');
  return (n < 0 ? '-' : '') + '$' + partes[0].replace(/\B(?=(\d{3})+(?!\d))/g, ',') + '.' + partes[1] + ' ' + String(moneda || 'MXN').toUpperCase();
}

/** Un texto de una línea, sin pasar de `max` caracteres (con «…» si se recortó). Cuenta y corta por carácter, no por
 *  unidad UTF-16: un emoji (par sustituto) nunca queda partido. */
export function recorte(texto, max) {
  const t = Array.from(String(texto == null ? '' : texto).replace(/\s+/g, ' ').trim());
  return t.length > max ? t.slice(0, Math.max(1, max - 1)).join('').trimEnd() + '…' : t.join('');
}

/**
 * "Cotización COT-0007". Un borrador no tiene folio: se reconoce por su primer concepto (lista_ajax lo trae como
 * `concepto`), «Borrador «Mantenimiento preventivo…»»; sin conceptos, por el día en que se guardó. El número interno
 * (#7) solo queda de último recurso: no le dice nada a quien lo busca.
 */
export function nombreDoc(fila) {
  const f = fila || {};
  if (f.tipo === 'borrador' && !f.folio_visible) {
    const c = recorte(f.concepto, 40);
    if (c) return 'Borrador «' + c + '»';
    const dia = fechaCorta(f.fecha);
    if (dia || f.concepto !== undefined) return 'Borrador sin conceptos' + (dia ? ' del ' + dia : '');
  }
  return (ETIQUETA[f.tipo] || 'Documento') + (f.folio_visible ? ' ' + f.folio_visible : ' #' + (Number(f.id) || 0));
}

/** Títulos de Crear que un documento puede llevar (Documento_previo_model::TITULOS). */
export const TITULOS = ['fa', 'rh', 'ra', 'rd', 'ne', 'nc'];

/**
 * ¿Puede este usuario entrar a Crear con ESTE documento (abrirlo, verlo en solo lectura o facturarlo)? abrir_ajax y
 * convertir_ajax exigen además de doc_* el crear_<titulo> del documento: la fila trae su título (lista_ajax, de su
 * encabezado; 'fa' si no) y roles.crear el mapa {fa: true, rh: false…} de la pantalla. Sin él, Abrir sería un callejón
 * con 403. Sin el mapa (configuración vieja) solo se sabe de crear_fa (roles.facturar).
 */
export function puedeAbrir(fila, roles) {
  const r = roles || {};
  const titulo = TITULOS.indexOf(fila && fila.titulo) !== -1 ? fila.titulo : 'fa';
  if (r.crear && typeof r.crear === 'object') return r.crear[titulo] === true;
  return titulo === 'fa' && r.facturar === true;
}

/** El estado para una frase «(está …)», concordando con el tipo: una remisión «facturada», un borrador «facturado»
 *  (Documento_previo_model::estado_en_frase). */
export function estadoEnFrase(fila) {
  const e = String((fila && fila.estado) || '');
  if (fila && fila.tipo === 'borrador') return e;
  return ({ facturado: 'facturada', cancelado: 'cancelada', abierto: 'abierta' })[e] || e;
}

/**
 * El folio: liga que abre el documento si el usuario puede entrar a Crear con él; si no, texto plano. Un borrador (sin
 * folio) se nombra por su primer concepto, recortado, y debajo cuántos más lleva; sin conceptos, lo dice.
 */
export function folioHtml(fila, roles) {
  const id = Number(fila.id) || 0;
  const borrador = fila.tipo === 'borrador' && !fila.folio_visible;
  const concepto = borrador ? recorte(fila.concepto, 80) : '';
  let texto = esc(fila.folio_visible || 'Borrador #' + id);
  let clase = 'fw-semibold font-mono text-nowrap';
  let titulo = '';
  if (borrador && (concepto || fila.concepto !== undefined)) {
    texto = concepto ? esc(concepto) : 'Borrador sin conceptos';
    clase = 'fw-semibold dp-borrador-concepto' + (concepto ? '' : ' text-muted');
    titulo = concepto ? ' title="' + esc(fila.concepto) + '"' : '';   // la línea se recorta con «…»: el texto completo al pasar
  }
  let html = puedeAbrir(fila, roles)
    ? '<a href="#" class="' + clase + '"' + titulo + ' data-accion="abrir" data-id="' + id + '">' + texto + '</a>'
    : '<span class="' + clase + '"' + titulo + '>' + texto + '</span>';
  const mas = borrador ? (Number(fila.conceptos) || 0) - 1 : 0;
  if (concepto && mas > 0) {
    html += '<small class="d-block text-muted">y ' + mas + (mas === 1 ? ' concepto más' : ' conceptos más') + '</small>';
  }
  if (fila.cfdi) {
    const sf = [fila.cfdi.serie, fila.cfdi.folio].filter(function (x) { return x; }).join('-');
    html += '<small class="d-block text-muted">Factura ' + esc(sf) + '</small>';
  }
  return html;
}

/**
 * El documento en que se convirtió un borrador al guardarlo como cotización o remisión (queda cerrado, no rechazado).
 * lista_ajax lo trae como `convertido_en`: el folio ('COT-0008') o {folio_visible, tipo, id}. null si no es un borrador
 * convertido o el dato no llegó. Sin tipo, se deduce de la serie (COT / REM).
 */
export function convertidoEn(fila) {
  if (!fila || fila.tipo !== 'borrador') return null;
  const c = fila.convertido_en;
  let folio = '';
  let tipo = '';
  if (typeof c === 'string') { folio = c.trim(); }
  else if (c && typeof c === 'object') { folio = String(c.folio_visible || c.folio || '').trim(); tipo = String(c.tipo || ''); }
  if (!folio) return null;
  if (tipo !== 'cotizacion' && tipo !== 'remision') {
    tipo = /^COT-/i.test(folio) ? 'cotizacion' : (/^REM-/i.test(folio) ? 'remision' : '');
  }
  return { folio: folio, tipo: tipo };
}

/**
 * Insignia del estado EFECTIVO (una cotización abierta o enviada fuera de su vigencia es «Vencida», calculado por el
 * servidor), con el género del tipo, y, en una cotización que sigue abierta o enviada, hasta cuándo vale. Una aceptada ya
 * no muestra la vigencia: el trato está hecho, y «vigente al» de una fecha pasada diría lo contrario. Un borrador
 * convertido dice «Convertido» y en qué (con liga a esa pestaña si `tipos`, las pestañas del usuario, la incluye).
 */
export function estadoBadge(fila, tipos) {
  const conv = convertidoEn(fila);
  if (conv || fila.estado_efectivo === 'convertido') {
    const c = estadoUi('borrador', 'convertido');
    let h = '<span class="badge ' + c.badge + '">' + esc(c.texto) + '</span>';
    if (conv) {
      // La liga lleva a la pestaña del documento nuevo, filtrada por su folio (solo si el usuario ve esa pestaña).
      const liga = conv.tipo && (tipos || []).indexOf(conv.tipo) !== -1;
      const folio = esc(conv.folio);
      h += '<small class="d-block text-muted">en ' + (liga
        ? '<a href="#" class="font-mono" data-accion="ver_convertido" data-tipo="' + conv.tipo + '" data-folio="' + folio + '">' + folio + '</a>'
        : '<span class="font-mono">' + folio + '</span>') + '</small>';
    }
    return h;
  }
  const estado = ESTADO_UI[fila.tipo] && ESTADO_UI[fila.tipo][fila.estado_efectivo] ? fila.estado_efectivo : fila.estado;
  const e = estadoUi(fila.tipo, estado);
  let html = '<span class="badge ' + e.badge + '">' + esc(e.texto) + '</span>';
  // Una vigencia ilegible no pinta «vigente al » con la fecha vacía: sin fecha, sin frase.
  const vig = fechaCorta(fila.vigencia);
  if (fila.tipo === 'cotizacion' && vig && editable(fila) && fila.estado !== 'aceptada') {
    html += '<small class="d-block text-muted">' + (fila.estado_efectivo === 'vencida' ? 'vigencia terminada el ' : 'vigente al ') + esc(vig) + '</small>';
  }
  return html;
}

/**
 * Acciones de una fila (spec §3.2). Todas: abrir (en solo lectura si ya no es editable; solo con el crear_<titulo> del
 * documento, ver puedeAbrir), PDF y duplicar; cancelar si es editable. Una cotización VENCIDA sigue editable (vencida =
 * abierta/enviada fuera de su vigencia, calculado): se acepta, se convierte y se duplica aunque el cliente conteste tarde
 * [RF-5]. Convertir en factura pide lo mismo que abrir (convertir_ajax exige crear_<titulo>). roles: doc_*, facturar
 * (crear_fa) y crear (el mapa por título).
 */
export function accionesDe(fila, roles) {
  const r = roles || {};
  const crear = puedeAbrir(fila, r);
  const out = crear ? ['abrir', 'pdf', 'duplicar'] : ['pdf', 'duplicar'];
  if (editable(fila) && fila.tipo === 'cotizacion') {
    out.push('enviar');
    if (fila.estado !== 'aceptada') out.push('aceptar');
    if (crear) out.push('convertir_factura');
    if (r.doc_remision) out.push('convertir_remision');
  }
  if (editable(fila) && fila.tipo === 'remision') {
    out.push('enviar');
    if (crear) out.push('convertir_factura');
  }
  if (editable(fila)) out.push('cancelar');
  return out;
}

export function etiquetaAccion(accion, fila) {
  if (accion === 'abrir') {
    if (!editable(fila)) return 'Consultar';
    return fila.tipo === 'borrador' ? 'Abrir y timbrar' : 'Abrir';
  }
  if (accion === 'pdf' && fila.tipo === 'remision') return 'Imprimir para firma';
  return TEXTO_ACCION[accion] || accion;
}

export function menuAcciones(fila, roles) {
  const id = Number(fila.id) || 0;
  const items = accionesDe(fila, roles).map(function (a) {
    return (a === 'cancelar' ? '<li><hr class="dropdown-divider"></li>' : '')
      + '<li><a class="dropdown-item' + (a === 'cancelar' ? ' text-danger' : '') + '" href="#" data-accion="' + a + '" data-id="' + id + '">'
      + '<i class="ti ' + ICONO[a] + ' me-2" aria-hidden="true"></i>' + esc(etiquetaAccion(a, fila)) + '</a></li>';
  }).join('');
  return '<div class="dropdown"><button type="button" class="btn btn-ghost btn-icon btn-sm dp-menu" data-bs-toggle="dropdown" aria-expanded="false"'
    + ' data-bs-popper-config=\'{"strategy":"fixed"}\' aria-label="' + esc('Acciones de ' + nombreDoc(fila)) + '"><i class="ti ti-dots-vertical" aria-hidden="true"></i></button>'
    + '<ul class="dropdown-menu dropdown-menu-end">' + items + '</ul></div>';
}

/** Celda Cliente: nombre y, debajo, su RFC. Sin nombre, el RFC ocupa su lugar; sin ninguno (un borrador), «Sin cliente».
 *  En celular el nombre se recorta a una línea (.dp-cliente-nombre, _documentos-previos.scss) y el RFC se esconde. */
export function clienteHtml(fila) {
  const nombre = String(fila.cliente || '').trim();
  const rfc = String(fila.rfc || '').trim();
  if (!nombre && !rfc) return '<span class="dp-cliente-nombre text-muted">Sin cliente</span>';
  if (!nombre) return '<span class="dp-cliente-nombre font-mono">' + esc(rfc) + '</span>';
  return '<span class="dp-cliente-nombre">' + esc(nombre) + '</span>'
    + (rfc ? '<small class="d-block text-muted font-mono dp-cliente-rfc">' + esc(rfc) + '</small>' : '');
}

/** Celda Fecha ('2026-10-10 12:34' → '10-oct-2026 12:34'). */
export function fechaHtml(valor) {
  return '<span class="font-mono text-nowrap">' + esc(fechaTexto(valor)) + '</span>';
}

/** Celda Total ('$1,234.50 MXN'). */
export function totalHtml(fila) {
  return esc(totalTexto(fila.total, fila.moneda));
}

/**
 * ¿La pantalla muestra «Facturar seleccionadas»? En Remisiones, si el usuario puede entrar a Crear con algún título
 * (roles.crear, el mapa crear_<titulo>; sin el mapa, crear_fa como antes). Cada casilla pide además el de SU remisión.
 */
export function puedeFacturarRemisiones(roles) {
  const r = roles || {};
  if (r.crear && typeof r.crear === 'object') return TITULOS.some(function (t) { return r.crear[t] === true; });
  return r.facturar === true;
}

/** Casilla de "Facturar seleccionadas": solo remisiones que todavía se pueden facturar y, con los roles de la pantalla,
 *  que el usuario puede llevar a Crear (la misma regla de Abrir, puedeAbrir: el crear_<titulo> de la remisión). */
export function casilla(fila, roles) {
  if (fila.tipo !== 'remision' || !editable(fila)) return '';
  if (roles && !puedeAbrir(fila, roles)) return '';
  return '<input type="checkbox" class="form-check-input dp-sel" aria-label="' + esc('Elegir ' + nombreDoc(fila)) + '">';
}

/** '' si se pueden facturar juntas; si no, el mensaje (los mismos textos que validar_conversion del servidor). */
export function validarSeleccion(filas) {
  const f = filas || [];
  if (!f.length) return 'Elige al menos una remisión.';
  if (f.length > MAX_SELECCION) return 'Puedes facturar hasta ' + MAX_SELECCION + ' remisiones a la vez.';
  for (let i = 0; i < f.length; i++) {
    if (f[i].tipo !== 'remision' || !editable(f[i])) {
      return (f[i].folio_visible || nombreDoc(f[i])) + ' ya está ' + estadoEnFrase(f[i]) + ' y se conserva como quedó; elige solo lo que sigue pendiente.';
    }
  }
  const clientes = new Set(f.map(function (x) { return (Number(x.cliente_id) || 0) + '|' + String(x.rfc || '').toUpperCase(); }));
  if (clientes.size > 1) return 'Para facturarlas juntas, elige remisiones del mismo cliente.';
  const monedas = new Set(f.map(function (x) { return String(x.moneda || '').toUpperCase(); }));
  if (monedas.size > 1) return 'Para facturarlas juntas, elige remisiones en la misma moneda.';
  // La factura se arma en la pantalla de Crear de la primera (su título y su tipo de comprobante).
  const tipos = new Set(f.map(function (x) {
    return (String(x.tipo_cfdi || 'I').toUpperCase()) + '|' + (TITULOS.indexOf(x.titulo) !== -1 ? x.titulo : 'fa');
  }));
  if (tipos.size > 1) return 'Para facturarlas juntas, elige remisiones del mismo tipo de comprobante.';
  return '';
}

/**
 * Lo que dice la pantalla junto a «Facturar seleccionadas»: ok = el botón se habilita; si no, el motivo. Sin elegir nada
 * no es un error, es la invitación a elegir. Con los roles de la pantalla, el botón se habilita con el crear_<titulo> de
 * las remisiones elegidas (convertir_ajax lo exige: la misma regla de Abrir), no con crear_fa.
 */
export function ayudaSeleccion(filas, roles) {
  const f = filas || [];
  if (!f.length) return { ok: false, texto: 'Elige las remisiones que quieres facturar en una sola factura.' };
  const error = validarSeleccion(f);
  if (error) return { ok: false, texto: error };
  if (roles && f.some(function (x) { return !puedeAbrir(x, roles); })) {
    return { ok: false, texto: 'Estas remisiones se facturan con el permiso de crear ese tipo de comprobante, que da quien administra tu cuenta; pídele que te dé acceso desde Configuración › Usuarios y permisos.' };
  }
  return { ok: true, texto: '' };
}

/** Lo que lista_ajax lee (sin columns/search de DataTables: el POST queda chico). */
export function paramsLista(data, tipo, filtros) {
  const f = filtros || {};
  const o = data && data.order && data.order[0];
  return {
    tipo: tipo, draw: data.draw, start: data.start, length: data.length, 'order[0][dir]': (o && o.dir === 'asc') ? 'asc' : 'desc',
    cliente: String(f.cliente || '').trim(), folio: String(f.folio || '').trim(),
    desde: f.desde || '', hasta: f.hasta || '', estado: f.estado || '',
  };
}

// ---------------------------------------------------------------------------------------------------------------------
// Correo de una cotización o remisión (modal «Enviar por correo»). Espejo de Correocola::destinos_documento: lo mismo
// que el servidor rechazaría se avisa en el modal sin gastar una petición (el servidor siempre vuelve a revisar).
// ---------------------------------------------------------------------------------------------------------------------
const CORREO_RE = /^[^\s@,;<>()[\]\\"]+@[^\s@,;<>()[\]\\"]+\.[^\s@,;<>()[\]\\"]+$/;

/** 'a@b.mx, c@d.mx;; e@f.mx ,' → ['a@b.mx', 'c@d.mx', 'e@f.mx'] (coma, punto y coma o espacio; sin vacíos). */
export function listaCorreos(texto) {
  return String(texto == null ? '' : texto).split(/[\s,;]+/).filter(Boolean);
}

/** null si Para y Copia oculta se pueden mandar; si no, {campo:'para'|'cco', msg} con el primer problema. */
export function validarCorreo(para, cco) {
  const campos = [['para', listaCorreos(para)], ['cco', listaCorreos(cco)]];
  if (!campos[0][1].length) return { campo: 'para', msg: 'Escribe al menos un correo en Para.' };
  for (let i = 0; i < campos.length; i++) {
    const mal = campos[i][1].filter(function (x) { return !CORREO_RE.test(x); })[0];
    if (mal !== undefined) return { campo: campos[i][0], msg: 'Revisemos «' + (mal.length > 50 ? mal.slice(0, 50) + '…' : mal) + '»: le falta algo para ser un correo completo.' };
  }
  for (let i = 0; i < campos.length; i++) {
    if (campos[i][1].length > MAX_CORREOS) {
      return { campo: campos[i][0], msg: 'Cada envío lleva hasta ' + MAX_CORREOS + ' direcciones en Para y ' + MAX_CORREOS + ' en copia oculta; para más, haz otro envío.' };
    }
  }
  return null;
}

/** La ayuda de «Para» de siempre (Correocola::MAX_DIRECCIONES_DOC). */
export const AYUDA_PARA = 'Hasta ' + MAX_CORREOS + ', separados por coma.';

/**
 * «Para» recuerda (critique 11-oct): lo que el modal precarga con la respuesta de destinatarios_ajax — el último envío
 * de ese documento o a ese cliente, o el correo de su catálogo — y la ayuda que dice de dónde salió. Sin respuesta útil,
 * el correo que ya traía la fila (el del catálogo). {para, cco, ayuda}.
 */
export function destinatariosPrevios(r, fila) {
  const ok = !!(r && String(r.code) === '200' && r.para);
  const para = ok ? String(r.para) : String((fila && fila.correo) || '').split(',').filter(Boolean).join(', ');
  const cco = ok && r.cco ? String(r.cco) : '';
  let ayuda = AYUDA_PARA;
  if (ok && r.origen === 'documento') {
    ayuda = 'Son los del último envío de ' + (fila && fila.tipo === 'remision' ? 'esta remisión' : 'esta cotización') + '. ' + AYUDA_PARA;
  } else if (ok && r.origen === 'cliente') {
    ayuda = 'Son los del último envío a este cliente. ' + AYUDA_PARA;
  }
  return { para: para, cco: cco, ayuda: ayuda };
}

/** '' si la fila se puede enviar; si no, por qué (una cotización vencida no se manda: se duplica con vigencia nueva). */
export function puedeEnviar(fila) {
  if (fila && fila.tipo === 'cotizacion' && fila.estado_efectivo === 'vencida') {
    return 'La vigencia de esta cotización terminó el ' + fechaTexto(fila.vigencia) + '. Duplícala y la envías con una vigencia nueva.';
  }
  return '';
}

// ---------------------------------------------------------------------------------------------------------------------
// Tope del plan gratuito (Adrián, 11-oct): 5 cotizaciones y 5 remisiones NUEVAS al mes, cada tipo por su lado; Premium sin
// tope; el borrador nunca. Duplicar y convertir una cotización en remisión cuentan. `cupos` es Documento_previo_model::
// cupos ({cotizacion: {ilimitado, limite, usados, restante, reinicio}, remision: {…}}, o null): llega en el CFG de la
// pantalla y en cada respuesta que crea un folio. Espejo de cfdi/documento-previo-logica.js (el bundle de Crear).
// ---------------------------------------------------------------------------------------------------------------------
export const CON_TOPE = ['cotizacion', 'remision'];
export const ORIGEN_UPSELL = 'documentosprevios';
const NOMBRES = { cotizacion: ['cotización', 'cotizaciones'], remision: ['remisión', 'remisiones'] };

function cupoDe(cupos, tipo) {
  const c = (cupos && typeof cupos === 'object' && CON_TOPE.indexOf(tipo) !== -1) ? cupos[tipo] : null;
  return (c && typeof c === 'object') ? c : null;
}

/** ¿Ya no quedan de este tipo este mes? Solo en el plan gratuito; Premium, el borrador o sin dato: nunca. */
export function cupoAgotado(cupos, tipo) {
  const c = cupoDe(cupos, tipo);
  return !!(c && !c.ilimitado && (Number(c.restante) || 0) <= 0);
}

/** «Te quedan 3 cotizaciones este mes.» para el plan gratuito; '' en Premium, en Borradores o sin dato. */
export function textoCupo(cupos, tipo) {
  const c = cupoDe(cupos, tipo);
  if (!c || c.ilimitado) return '';
  const n = NOMBRES[tipo];
  const r = Math.max(0, Number(c.restante) || 0);
  if (r > 0) return (r === 1 ? 'Te queda 1 ' + n[0] : 'Te quedan ' + r + ' ' + n[1]) + ' este mes.';
  const lim = Number(c.limite) || 0;
  return 'Este mes ya usaste tus ' + lim + ' ' + n[1] + ' del plan gratuito' + (c.reinicio ? '; el ' + c.reinicio + ' tienes ' + lim + ' nuevas.' : '.');
}

/** El cupo que trae la respuesta (duplicar, convertir y el 402 lo traen), o el que ya se tenía. */
export function cupoTrasRespuesta(cupos, r) {
  return (r && r.cupo && typeof r.cupo === 'object') ? r.cupo : cupos;
}

/** Lo que FD.upsell necesita para el tope de `tipo`: origen medible, gancho y {n} (el límite) y {fecha} (cuándo se renueva). */
export function upsellDeTipo(cupos, tipo) {
  const c = cupoDe(cupos, tipo) || {};
  return { origen: ORIGEN_UPSELL, gancho: 'docprev-' + tipo, ctx: { n: Number(c.limite) || 5, fecha: String(c.reinicio || '') } };
}

/** El 402 del tope (duplicar o convertir en remisión): lo mismo, con lo que dice el servidor; null si no es un upsell. */
export function upsellDeRespuesta(r) {
  if (!r || String(r.code) !== '402' || !r.upsell) return null;
  const gancho = /^docprev-(cotizacion|remision)$/.test(String(r.gancho || '')) ? String(r.gancho) : 'docprev-cotizacion';
  return { origen: ORIGEN_UPSELL, gancho: gancho, ctx: { n: Number(r.limite) || 5, fecha: String(r.reinicio || '') } };
}

// ---------------------------------------------------------------------------------------------------------------------
// Preguntas de la pantalla (guía de tono de Adrián, 11-oct): el título dice qué pasa y los botones lo que hacen, nunca
// «Sí / No»; el texto explica la consecuencia de cada uno.
// ---------------------------------------------------------------------------------------------------------------------

/** El 409 de «ya está abierto en otra ventana de Crear» (abrir y convertir): título y botones. El servidor manda `titulo`
 *  y `dejar` («Dejarla allá», con el género y el número del documento); si faltaran, unos neutros. */
export function botonesOtraVentana(r) {
  const x = r || {};
  return {
    titulo: String(x.titulo || 'Este documento ya está abierto en otra ventana'),
    continuar: 'Continuar aquí',
    dejar: String(x.dejar || 'Dejarlo allá'),
  };
}

/** «Convertir en remisión»: qué se crea y qué le pasa a la cotización. */
export function confirmacionRemision(fila) {
  const folio = (fila && fila.folio_visible) || 'la cotización';
  return {
    titulo: 'Convertir ' + folio + ' en remisión',
    texto: 'Creamos una remisión nueva con los mismos conceptos y ' + folio + ' queda como Remisionada, lista para consulta.',
    si: 'Crear la remisión',
    no: 'Dejarla como está',
  };
}

/** «Cancelar»: el documento se conserva completo (borrado lógico) y ya no se edita ni se factura. */
export function confirmacionCancelar(fila) {
  const masc = fila && fila.tipo === 'borrador';
  const nombre = nombreDoc(fila || {});
  return {
    titulo: 'Cancelar ' + nombre,
    texto: (masc ? 'Se conserva completo y lo encuentras' : 'Se conserva completa y la encuentras')
      + ' con el filtro «' + nombreFiltro(masc ? 'borrador' : 'cotizacion', 'cancelado') + '»; desde ese momento queda solo para consulta.',
    si: masc ? 'Cancelar el borrador' : (fila && fila.tipo === 'remision' ? 'Cancelar la remisión' : 'Cancelar la cotización'),
    no: masc ? 'Conservarlo activo' : 'Conservarla activa',
  };
}

// ---------------------------------------------------------------------------------------------------------------------
// La lista (critique 11-oct): el botón «Nueva …» de cada pestaña, los estados vacíos, el pie propio, los filtros en
// celular, el esqueleto de carga, las casillas solo con remisiones por facturar, las pestañas con flechas y DataTables en
// español. El montaje vive en la vista (noble/documentosprevios/index.php).
// ---------------------------------------------------------------------------------------------------------------------
const NOMBRES_DOC = { cotizacion: ['cotización', 'cotizaciones'], remision: ['remisión', 'remisiones'], borrador: ['borrador', 'borradores'] };
const NUEVO = { cotizacion: 'Nueva cotización', remision: 'Nueva remisión', borrador: 'Nuevo borrador' };

function miles(n) { return String(n).replace(/\B(?=(\d{3})+(?!\d))/g, ','); }

/** El estado como adjetivo del documento, en singular o plural: «pendiente», «facturadas», «cancelados o convertidos». */
function adjetivo(tipo, estado, cuantos) {
  const i = cuantos === 1 ? 0 : 1;
  if (estado === ESTADO_DEFECTO) return i ? 'pendientes' : 'pendiente';
  if (tipo === 'borrador' && estado === 'cancelado') return i ? 'cancelados o convertidos' : 'cancelado o convertido';
  const e = ESTADO_UI[tipo] && ESTADO_UI[tipo][estado];
  return e ? e[i].toLowerCase() : '';
}

/** El botón de la pestaña: «Nueva cotización», «Nueva remisión» o «Nuevo borrador». */
export function textoNuevo(tipo) { return NUEVO[tipo] || ''; }

/** A dónde lleva: Crear en modo documento y con captura limpia (v2/cfdi/crear/fa?doc=<tipo>&nuevo=1: suelta lo que
 *  estuviera abierto y estrena keydocument, contrato del Frente B); sin un tipo válido, Crear tal cual. Abrir un documento
 *  (abrir_ajax) no lleva nuevo=1: abre ESE documento. */
export function hrefNuevo(urlCrear, tipo) {
  const base = String(urlCrear || '');
  if (TIPOS.indexOf(tipo) === -1) return base;
  return base + (base.indexOf('?') === -1 ? '?' : '&') + 'doc=' + encodeURIComponent(tipo) + '&nuevo=1';
}

/** Lo que dice una pestaña que todavía no tiene documentos: {titulo, texto}. A quien entra a Crear le dice por dónde
 *  empezar (el botón de la pestaña); a quien no, solo qué aparecerá aquí. El texto es un literal con <strong>. */
export function vacioDe(tipo, puedeCrear) {
  const n = NOMBRES_DOC[tipo] || NOMBRES_DOC.cotizacion;
  const texto = {
    cotizacion: 'Empieza con <strong>Nueva cotización</strong>: la capturas, la guardas y se la envías a tu cliente.',
    remision: 'Empieza con <strong>Nueva remisión</strong>, o convierte en remisión una cotización.',
    borrador: 'Empieza con <strong>Nuevo borrador</strong>, o guarda como borrador lo que capturas en Crear CFDI.',
  }[tipo];
  return { titulo: 'Aquí aparecerán tus ' + n[1], texto: puedeCrear && texto ? texto : '' };
}

/** Lo que dice la tabla cuando hay documentos pero ninguno pasa los filtros: {titulo, texto}. Solo con el estado
 *  (p. ej. «Pendientes», la de omisión) lo nombra; con cliente, folio o fechas, invita a revisar la búsqueda. */
export function sinCoincidencias(tipo, filtros) {
  const f = filtros || {};
  const n = NOMBRES_DOC[tipo] || NOMBRES_DOC.cotizacion;
  const fem = tipo !== 'borrador';
  const otros = ['cliente', 'folio', 'desde', 'hasta'].some(function (k) { return String(f[k] || '').trim() !== ''; });
  if (!otros && f.estado) {
    return { titulo: (fem ? 'Ninguna ' : 'Ningún ') + n[0] + ' ' + adjetivo(tipo, f.estado, 1),
      texto: 'Para ver ' + (fem ? 'las demás' : 'los demás') + ', elige otro estado en el filtro.' };
  }
  return { titulo: (fem ? 'Ninguna ' : 'Ningún ') + n[0] + ' coincide con tu búsqueda',
    texto: 'Revisa el cliente, el folio, las fechas o el estado que elegiste.' };
}

/**
 * El pie de la tabla: «2 cotizaciones pendientes», «1 borrador», «26–50 de 120 remisiones facturadas». `estado` es el
 * filtro con que se pidió la página ('' = todas); desde y hasta, la posición (1-based) de la primera y la última fila.
 * Sin documentos, nada: el estado vacío ya lo dice.
 */
export function textoPie(total, tipo, estado, desde, hasta) {
  const t = Number(total) || 0;
  if (t <= 0) return '';
  const n = NOMBRES_DOC[tipo] || NOMBRES_DOC.cotizacion;
  const adj = estado ? adjetivo(tipo, estado, t) : '';
  const cuenta = miles(t) + ' ' + (t === 1 ? n[0] : n[1]) + (adj ? ' ' + adj : '');
  const a = Number(desde) || 0;
  const b = Number(hasta) || 0;
  if (!(a > 1 || (b > 0 && b < t))) return cuenta;
  return (a === b ? miles(a) : miles(a) + '–' + miles(b)) + ' de ' + cuenta;
}

/** Cuántos filtros además de la búsqueda están puestos (celular: «Filtros (2)»). El estado cuenta si no es el de omisión. */
export function cuentaFiltros(filtros) {
  const f = filtros || {};
  let n = 0;
  ['folio', 'desde', 'hasta'].forEach(function (k) { if (String(f[k] || '').trim() !== '') n++; });
  if (String(f.estado == null ? '' : f.estado) !== ESTADO_DEFECTO) n++;
  return n;
}

export function textoBotonFiltros(n) { return n > 0 ? 'Filtros (' + n + ')' : 'Filtros'; }

/** El encabezado de la primera columna de datos: el folio, o en Borradores (no tienen) su primer concepto. */
export function encabezadoFolio(tipo) { return tipo === 'borrador' ? 'Concepto' : 'Folio'; }

/** La nota bajo la tabla: dónde queda lo cancelado (con el nombre del filtro) y, solo en Cotizaciones, que una vencida
 *  se acepta y se convierte igual. */
export function notaPie(tipo) {
  if (tipo === 'borrador') return 'Cancelar conserva el borrador completo: lo encuentras con el filtro «' + nombreFiltro('borrador', 'cancelado') + '».';
  if (tipo === 'remision') return 'Cancelar conserva la remisión completa: la encuentras con el filtro «' + nombreFiltro('remision', 'cancelado') + '».';
  return 'Cancelar conserva la cotización completa: la encuentras con el filtro «' + nombreFiltro('cotizacion', 'cancelado')
    + '». Una cotización vencida se puede aceptar y convertir igual.';
}

/** Columnas de la tabla, en orden (la clase dp-c-<col> va en th y td: el celular las acomoda en tarjeta por clase). */
export const COLUMNAS = ['sel', 'folio', 'fecha', 'cliente', 'total', 'estado', 'acc'];
const ANCHO_SK = { folio: [70, 60, 80], fecha: [80, 70, 75], cliente: [75, 55, 90, 65], total: [60, 70, 55], estado: [45, 55, 50] };

/** Filas esqueleto mientras llega la lista (la forma de lo que viene, no «Procesando…»). Sin la columna de casillas si
 *  está oculta. Decorativas: aria-hidden (la tabla dice aria-busy mientras carga). */
export function filasEsqueleto(n, conCasilla) {
  const cols = COLUMNAS.filter(function (c) { return c !== 'sel' || conCasilla; });
  let html = '';
  for (let i = 0; i < n; i++) {
    html += '<tr class="dp-sk-fila" aria-hidden="true">' + cols.map(function (c) {
      const w = ANCHO_SK[c] ? ANCHO_SK[c][i % ANCHO_SK[c].length] : 0;
      return '<td class="dp-c-' + c + '">' + (w ? '<span class="dp-sk" style="width:' + w + '%"></span>' : '') + '</td>';
    }).join('') + '</tr>';
  }
  return html;
}

/**
 * Tras una acción que saca filas (cancelar la última de una página que no es la primera), la página pedida puede quedar
 * vacía aunque haya documentos: el índice de la última página que sí existe, para saltar ANTES de pintar (sin el vacío
 * ni «51–50 de 50» un instante). null si la página trae filas, es la primera o no hay nada que mostrar.
 */
export function paginaQueExiste(inicio, largo, total, filas) {
  const s = Number(inicio) || 0;
  const l = Number(largo) || 0;
  const t = Number(total) || 0;
  if ((Number(filas) || 0) > 0 || s <= 0 || t <= 0 || l <= 0) return null;
  return Math.max(0, Math.ceil(t / l) - 1);
}

/** ¿Alguna fila de esta página se puede elegir para «Facturar seleccionadas»? Si no, la barra y la columna se ocultan. */
export function hayElegibles(filas, roles) {
  return (filas || []).some(function (f) { return f && casilla(f, roles) !== ''; });
}

/** Pestañas con teclado (patrón tablist): ← y → recorren en círculo, Inicio y Fin van a la primera y la última. El índice
 *  de la pestaña a la que se pasa, o null si la tecla no es de navegación. Con un modificador (`mods`, el evento: Alt+←
 *  es «atrás», Cmd+← y Ctrl+Inicio mueven el cursor o la página) la tecla es del navegador: null y no se toca. */
export function tabVecina(i, n, tecla, mods) {
  const m = mods || {};
  if (m.altKey || m.ctrlKey || m.metaKey || m.shiftKey) return null;
  if (!(n > 0)) return null;
  if (tecla === 'ArrowRight') return (i + 1) % n;
  if (tecla === 'ArrowLeft') return (i - 1 + n) % n;
  if (tecla === 'Home') return 0;
  if (tecla === 'End') return n - 1;
  return null;
}

/**
 * Textos de DataTables 2 en español, sin pedir el archivo de idioma (se pedía aparte y la tabla nacía en inglés:
 * «Activate to invert sorting»). La única columna que se ordena (Fecha) alterna de más reciente a más antigua, así que
 * «quitar el orden» de DataTables también invierte. Vacío, sin coincidencias y el pie los pone la vista en cada respuesta.
 */
export function idiomaTabla() {
  return {
    emptyTable: '', zeroRecords: '', loadingRecords: '', processing: '',
    info: '', infoEmpty: '', infoFiltered: '', infoPostFix: '', thousands: ',', decimal: '.',
    entries: { _: 'documentos', 1: 'documento' },
    paginate: { first: '«', previous: '‹', next: '›', last: '»' },
    aria: {
      orderable: ': ordenar', orderableReverse: ': invertir el orden', orderableRemove: ': invertir el orden',
      paginate: { first: 'Primera página', previous: 'Página anterior', next: 'Página siguiente', last: 'Última página', number: 'Página ' },
    },
  };
}
